import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, X, Send, Loader2, Bot } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Merhaba! Ben FairStand Asistan. Fuar standı sektörü hakkında her türlü sorunuzu yanıtlayabilirim. Stand tasarımı, maliyetler, lojistik, teknik gereksinimler... Nasıl yardımcı olabilirim?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMsg,
      });
      setSessionId(res.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.data.response },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Bir hata oluştu. Lütfen tekrar deneyin.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-20 right-6 z-50" data-testid="chatbot-container">
      {/* Chatbot Window */}
      {isOpen && (
        <div
          className="chatbot-enter w-80 md:w-96 bg-stone-900 text-stone-50 border border-stone-700 shadow-2xl mb-4 flex flex-col"
          style={{ height: "440px" }}
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-700">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-[#1B4F72] flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-white">
                  FairStand Asistan
                </p>
                <p className="font-mono text-[9px] text-stone-400">
                  Sektörel Bilgi Bankası
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-stone-400 hover:text-white hover:bg-stone-800 h-7 w-7"
              onClick={() => setIsOpen(false)}
              data-testid="chatbot-close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 px-4 py-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-message-${idx}`}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2.5 text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-[#1B4F72] text-white"
                        : "bg-stone-800 text-stone-200 border border-stone-700"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-stone-800 border border-stone-700 px-3 py-2.5">
                    <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <form
            onSubmit={sendMessage}
            className="border-t border-stone-700 p-3 flex gap-2"
            data-testid="chatbot-input-form"
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Sorunuzu yazın..."
              className="flex-1 bg-stone-800 border border-stone-700 text-white px-3 py-2 text-sm placeholder:text-stone-500 focus:outline-none focus:border-[#1B4F72] transition-colors"
              disabled={loading}
              data-testid="chatbot-input"
            />
            <Button
              type="submit"
              size="icon"
              disabled={loading || !input.trim()}
              className="bg-[#1B4F72] hover:bg-[#154360] text-white h-9 w-9 rounded-none"
              data-testid="chatbot-send"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </form>
        </div>
      )}

      {/* Toggle Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={`rounded-none w-14 h-14 shadow-lg transition-all ${
          isOpen
            ? "bg-stone-700 hover:bg-stone-600"
            : "bg-[#1B4F72] hover:bg-[#154360]"
        }`}
        data-testid="chatbot-toggle"
      >
        {isOpen ? (
          <X className="w-5 h-5 text-white" />
        ) : (
          <MessageCircle className="w-5 h-5 text-white" />
        )}
      </Button>
    </div>
  );
};
